import { useEffect, useState } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { z } from "zod";
import { getAllLevel } from "../../../api/pedagogicElement";
import {
  getEvaluationPeriodById,
  updateEvaluationPeriod,
} from "../../../api/generalParam";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  periodID: UUID;
  onClose: () => void;
  onSaved?: () => void;
};

interface Level {
  id: string;
  label: string;
}

const EditEvaluationPeriodModal = ({
  periodID,
  show,
  onClose,
  onSaved,
}: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear, setSelectedYear } = useAcademicYear();
  const [levels, setLevels] = useState<Level[]>([]);

  //Schéma de notre formulaire
  const createSchema = z.object({
    label: z.string().nonempty("Le libellé de la classe est requis"),
    abbreviation: z.string().nonempty("L'abréviation est requis"),
    id_level: z.string().nonempty("Le niveau d'étude est requis"),
    startDate: z.string().nonempty("Date de début requise"),
    endDate: z.string().nonempty("Date de fin requise"),
    statut: z.boolean(),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await updateEvaluationPeriod(data, periodID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Période d'évaluation mise à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      /// Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show || !periodID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchEvaluationPeriod = async () => {
      try {
        const response = await getEvaluationPeriodById(periodID);
        if (!response) {
          // Si aucune année trouvé
          throw new Error("Période introuvable");
        }

        reset({
          // Si année trouvé on rempli les champs du formulaire
          label: response.data.label,
          abbreviation: response.data.abbreviation,
          id_level: response.data.id_level,
          startDate: response.data.startDate,
          endDate: response.data.endDate,
          statut: response.data.statut,
        });
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
          error.message ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Une erreur s'est produit, Réessayer plus tard",
        );
      }
    };

    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    getAllLevel(
      selectedYear?.id,
      buildPermissionContext(Resource.EVAL_PERIOD, Action.UPDATE),
    )
      .then((levels) => {
        setLevels(levels.data);
        fetchEvaluationPeriod();
      })
      .catch((error) => {
        toast.error("Une erreur s'est produite, réessayez plus tard.");
      });
  }, [show, periodID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifer une Période</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Niveau d'étude</label>
                  <select
                    {...register("id_level")}
                    aria-label="Default select example"
                    className={
                      errors.id_level ? "form-select is-invalid" : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {levels.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  {errors.id_level && (
                    <div className="invalid-feedback">
                      {errors.id_level.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Semestre 1"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Abréviation
                  </label>
                  <input
                    {...register("abbreviation")}
                    type="text"
                    className={
                      errors.abbreviation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: S1 ou S2"
                  />
                  {errors.abbreviation && (
                    <div className="invalid-feedback">
                      {errors.abbreviation.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer02">
                    Date Début
                  </label>
                  <input
                    {...register("startDate")}
                    type="date"
                    className={
                      errors.startDate
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Début de la période"
                  />
                  {errors.startDate && (
                    <div className="invalid-feedback">
                      {errors.startDate.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer03">
                    Date Fin
                  </label>
                  <input
                    {...register("endDate")}
                    type="date"
                    className={
                      errors.endDate
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Fin de la période"
                  />
                  {errors.endDate && (
                    <div className="invalid-feedback">
                      {errors.endDate.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="form-check form-check-lg form-switch mb-3">
                  <label className="form-check-label" htmlFor="switch-lg">
                    Statut de la période
                  </label>
                  <input
                    {...register("statut")}
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="switch-lg"
                  />
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              // disabled={isSubmitting||!selectedYear?.statut}
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditEvaluationPeriodModal,
  [buildPermission(Resource.EVAL_PERIOD, Action.UPDATE)],
  "masquer",
);
